import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListIcon from "@mui/icons-material/FilterList";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { useCallback, useRef, useState } from "react";
import { Link, generatePath } from "react-router-dom";
import { ProjectTool } from "../../../../Common/api/models/ProjectTool";
import ActionConfirmation from "../../../../Common/components/ActionConfirmation/ActionConfirmation";
import NavigationPrompt from "../../../../Common/components/NavigationPrompt/NavigationPrompt";
import AdvancedFiltersDialog from "../../../../Common/components/AdvancedFiltersDialog/AdvancedFiltersDialog";
import { ToolCapability, User } from "../../../../Common/models/FormModels";
import {
  FetchDataRequest,
  FetchDataResult,
} from "../../../../Common/models/grid";
import {
  AdvancedFiltersState,
  AdvancedFilterOptions,
  defaultAdvancedFiltersState,
  buildAdvancedFiltersDto,
  hasActiveAdvancedFilters,
} from "../../../../Common/models/advancedFilters";
import { PROJECT_USERS_FILTER_CONFIG } from "../../../../Common/models/advancedFilterConfig";
import { useProjectUsersAdvancedFilters } from "../../hooks/useProjectUsersAdvancedFilters";
import { ProjectAccess } from "../../api/models/ProjectAccess";
import { TableSortFilterForPagination } from "../../api/models/Table";
import useProjectUsersForm from "../../hooks/useProjectUsersForm";
import AddProjectUsersPopup from "../AddProjectUsersPopup/AddProjectUsersPopup";
import EditProjectAccessPopup from "../EditProjectAccessPopup/EditProjectAccessPopup";
import styles from "../ProjectToolsTable/ProjectToolsTable.module.scss";
import ProjectUsersTable from "../ProjectUsersTable/ProjectUsersTable";
import { debounce } from "lodash";
import SelfonboardingAlert from "../../../../Common/components/SelfonboardingAlert/SelfonboardingAlert";
import { toolsDefaultRoles } from "../../../../Common/models/tools";
import { usePersistentState } from "../../../../Common/hooks/usePersistentState";
import { SettingsKeys } from "../../../../Common/models/storageSettingsKeys";
import { Order } from "../../api/models/ProjectUsersTable";
import routes from "../../../../Common/consts/routes";
import { usePendingRequestsCountForProject } from "../../../SelfOnboarding/hooks/usePendingRequestsCountForProject";
import NotificationsIcon from "@mui/icons-material/Notifications";

interface ProjectUsersFormProps {
  projectId: string;
  projectName: string;
  packageId: string;
  useCustomRoleMapping: boolean;
  userCanEdit: boolean;
  projectTools: ProjectTool[];
  projectAccess: ProjectAccess;
  fetchGridData: (
    projectId: string,
    request: FetchDataRequest,
    advancedFilters?: AdvancedFilterOptions
  ) => Promise<FetchDataResult>;
}
const ProjectUsersForm = ({
  projectId,
  projectName,
  packageId,
  useCustomRoleMapping,
  userCanEdit,
  projectTools,
  projectAccess,
  fetchGridData,
}: ProjectUsersFormProps) => {
  const { count: pendingRequestsCount, isLoading: isPendingCountLoading } =
    usePendingRequestsCountForProject(projectId);

  const [localStorageSettings, setLocalStorageSettings] = usePersistentState(
    localStorage,
    SettingsKeys.ProjectUsersGrid,
    {
      order: "asc",
      orderBy: "name",
      rowsPerPage: 10,
    }
  );

  const [tableSortFilterForPagination, setTableSortFilterForPagination] =
    useState<TableSortFilterForPagination>({
      order: localStorageSettings["order"] as Order,
      orderBy: localStorageSettings["orderBy"] as keyof User,
      page: 1,
      rowsPerPage: localStorageSettings["rowsPerPage"],
      search: "",
    });

  const [totalRows, setTotalRows] = useState(0);

  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef();

  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [dialogFilterState, setDialogFilterState] =
    useState<AdvancedFiltersState>(defaultAdvancedFiltersState);

  const [appliedFilters, setAppliedFilters] =
    useState<AdvancedFilterOptions>(buildAdvancedFiltersDto(defaultAdvancedFiltersState));

  const { form, append, handleDelete, handleProjectUsersSubmit, isSaving } =
    useProjectUsersForm(
      [],
      tableSortFilterForPagination,
      setTotalRows,
      projectTools,
      packageId,
      appliedFilters
    );

  const {
    getValues,
    formState: { isDirty },
  } = form;

  const hasActiveFilters = hasActiveAdvancedFilters(appliedFilters);

  const { data: filterOptions, isLoading: isFilterOptionsLoading } =
    useProjectUsersAdvancedFilters(projectId);

  const handleApplyAdvancedFilters = (filterState: AdvancedFiltersState) => {
    setDialogFilterState(filterState);
    setAppliedFilters(buildAdvancedFiltersDto(filterState));
    setTableSortFilterForPagination((prev) => ({ ...prev, page: 1 }));
  };
  const submitSearch = () => {
    const searchValue = searchInputRef.current!["value"];
    setSearchValue(searchValue);
    setShowConfirmationModal(false);
  };

  const performSearch = () => {
    setTableSortFilterForPagination({
      ...tableSortFilterForPagination,
      page: 1,
    });
    handleShowActionConfirmation(() => submitSearch());
  };

  const debouncedHandleSearchKeyDown = useCallback(
    debounce(() => {
      performSearch();
    }, 500),
    []
  );

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof User
  ) => {
    const handleSortChange = () => {
      const isAsc =
        tableSortFilterForPagination.orderBy === property &&
        tableSortFilterForPagination.order === "asc";

      setTableSortFilterForPagination({
        ...tableSortFilterForPagination,
        page: 1,
        orderBy: property,
        order: isAsc ? "desc" : "asc",
      });
      setShowConfirmationModal(false);
      setLocalStorageSettings({
        rowsPerPage: tableSortFilterForPagination.rowsPerPage,
        orderBy: property,
        order: isAsc ? "desc" : "asc",
      });
    };

    handleShowActionConfirmation(() => handleSortChange());
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    const changePageChange = () => {
      setTableSortFilterForPagination({
        ...tableSortFilterForPagination,
        page: newPage + 1,
      });
      setShowConfirmationModal(false);
    };

    handleShowActionConfirmation(() => changePageChange());
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rowsPerPage = parseInt(event.target.value, 10);

    const handleRowsChange = () => {
      {
        setTableSortFilterForPagination({
          ...tableSortFilterForPagination,
          page: 1,
          rowsPerPage: rowsPerPage,
        });
        setLocalStorageSettings({
          orderBy: tableSortFilterForPagination.orderBy,
          order: tableSortFilterForPagination.order,
          rowsPerPage: rowsPerPage,
        });
        setShowConfirmationModal(false);
      }
    };
    handleShowActionConfirmation(() => handleRowsChange());
  };
  const handleShowActionConfirmation = (funcToHandle: any) => {
    if (isDirty) {
      setTableModalFunc(() => funcToHandle);
      setShowConfirmationModal(true);
    } else {
      funcToHandle();
    }
  };

  const setToolCustomRole = (capabilityToolKey: string) => {
    return toolsDefaultRoles[capabilityToolKey] ?? toolsDefaultRoles["DEFAULT"];
  };

  const addUserTemplate: ToolCapability[] = projectTools
    ? projectTools?.map((pt) => {
        const toolCreated = !!pt.toolProjectId || pt.isManual;
        // Temporarily block Confluence and Jira from being pre-selected when adding users
        const toolCap: ToolCapability = {
          availableRoles: pt.roles ?? [],
          capabilityToolKey: pt.capabilityToolKey,
          cost: "",
          hasAccess: toolCreated ? pt.optOut : false,
          status: "",
          toolCapRoleId: "",
          toolName: pt.toolName,
          toolDisplayName: pt.toolDisplayName,
          toolId: pt.toolId,
          toolProjectId: pt.toolProjectId,
          capabilityId: pt.capabilityId,
          toolCustomRole: setToolCustomRole(pt.capabilityToolKey),
          toolCreationCompleted: toolCreated,
        };
        return toolCap;
      })
    : [];

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isProjectAccessModalOpen, setProjectAccessModalIsOpen] =
    useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [tableModalFunc, setTableModalFunc] = useState<() => void>(
    () => handleChangePage
  );

  return (
    <>
      <NavigationPrompt
        when={isDirty}
        title="Do you want to leave this page?"
        description="You have unsaved changes that will be lost if you decide to continue."
      />

      <ActionConfirmation
        title={"Do you want to change page or filters?"}
        description={
          "You have unsaved changes that will be lost if you decide to continue."
        }
        when={isDirty && showConfirmationModal}
        confirm={tableModalFunc}
        cancel={() => setShowConfirmationModal(false)}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSaving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {!projectAccess.projectIsHidden && (
        <div style={{ width: "92%" }}>
          <SelfonboardingAlert />
        </div>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width={"92%"}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant={"h6"}>Users ({totalRows})</Typography>
          {userCanEdit && pendingRequestsCount > 0 && (
            <Chip
              component={Link}
              to={`${generatePath(routes.requests, {
                tab: "pending",
              })}?search=${encodeURIComponent(projectName)}`}
              icon={<NotificationsIcon />}
              label={`${pendingRequestsCount} Pending Request${
                pendingRequestsCount !== 1 ? "s" : ""
              }`}
              color="warning"
              size="small"
              clickable
              sx={{ cursor: "pointer" }}
            />
          )}
        </Box>
        {userCanEdit && (
          <div style={{ textAlign: "right" }}>
            {!projectAccess.projectIsHidden && (
              <Button
                variant="outlined"
                onClick={() => setProjectAccessModalIsOpen((open) => !open)}
                size="medium"
                sx={{ marginRight: "8px" }}
              >
                EDIT PROJECT ACCESS
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => setIsAddUserModalOpen((open) => !open)}
              size="medium"
            >
              ADD USERS
            </Button>
          </div>
        )}
      </Box>

      <AddProjectUsersPopup
        projectId={projectId}
        open={isAddUserModalOpen}
        handleClose={() => setIsAddUserModalOpen(false)}
        handleAddUser={append}
        getValues={getValues}
        toolCaps={addUserTemplate}
        useCustomRoleMapping={useCustomRoleMapping}
      />
      <EditProjectAccessPopup
        open={isProjectAccessModalOpen}
        handleClose={() => setProjectAccessModalIsOpen(false)}
        projectAccess={projectAccess}
      />
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          mt: 2,
          mb: 3,
          ml: -1,
        }}
      >
        <IconButton
          type="submit"
          sx={{ p: "5px", marginTop: "14px" }}
          aria-label="search"
          onClick={performSearch}
        >
          <SearchRoundedIcon />
        </IconButton>
        <TextField
          sx={{
            width: 450,
            mr: 1,
            "& .MuiFormLabel-root.MuiInputLabel-root": {
              color: "rgba(0, 0, 0, 0.38)",
            },
            "& .MuiInputBase-root.MuiInput-root.MuiInput-underline:before": {
              borderBottom: "1px solid rgba(0, 0, 0, 0.38)",
            },
          }}
          type="search"
          variant="standard"
          label={"Search name, Heiway account, email"}
          onKeyDown={debouncedHandleSearchKeyDown}
          inputRef={searchInputRef}
        />
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          endIcon={
            hasActiveFilters ? (
              <WarningAmberIcon sx={{ color: "#f9a825" }} />
            ) : undefined
          }
          onClick={() => setIsAdvancedFiltersOpen(true)}
          sx={{ alignSelf: "flex-end", mb: "2px" }}
        >
          Advanced Filters
        </Button>
      </Stack>

      <AdvancedFiltersDialog
        open={isAdvancedFiltersOpen}
        filters={dialogFilterState}
        filterOptions={filterOptions}
        filterConfig={PROJECT_USERS_FILTER_CONFIG}
        isLoading={isFilterOptionsLoading}
        onApply={handleApplyAdvancedFilters}
        onClose={() => setIsAdvancedFiltersOpen(false)}
      />

      <form>
        <ProjectUsersTable
          projectId={projectId}
          packageId={packageId}
          useCustomRoleMapping={useCustomRoleMapping}
          form={form}
          userCanEdit={userCanEdit}
          handleDelete={handleDelete}
          searchValue={searchValue}
          fetchTableData={fetchGridData}
          tableSortFilterForPagination={tableSortFilterForPagination}
          handleRequestSort={handleRequestSort}
          advancedFilters={appliedFilters}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalRows={totalRows}
          setTotalRows={setTotalRows}
          projectTools={projectTools}
        />
        {isDirty && userCanEdit && (
          <div className={styles.toolDetails__footer}>
            <Button
              variant="contained"
              onClick={() => handleProjectUsersSubmit()}
              size="large"
              sx={{ mr: 3 }}
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </>
  );
};
export default ProjectUsersForm;
